import { useMemo } from 'react';

/**
 * Incense smoke rising in the background. CSS-only, several heavily-blurred
 * warm plumes drift upward from the foot of the section, swaying and
 * dissipating like censer smoke catching the light. Parameters are stable
 * per-mount so the pattern doesn't reshuffle on re-render. Hidden entirely
 * under prefers-reduced-motion (handled in landing.css).
 */
type Props = {
  count?: number;
  /** Where the plumes originate horizontally, as % anchors. */
  className?: string;
};

type Plume = {
  x: number;
  delay: number;
  dur: number;
  drift: number;
  scale: number;
  width: number;
  gold: boolean;
};

function rand(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export function IncenseSmoke({ count = 9, className }: Props) {
  const plumes = useMemo<Plume[]>(() => {
    const r = rand(1453);
    return Array.from({ length: count }, (_, i) => ({
      x: 6 + r() * 88,
      delay: r() * 26,
      dur: 22 + r() * 18,
      drift: (r() - 0.5) * 220,
      scale: 1.6 + r() * 1.8,
      width: 240 + Math.round(r() * 260),
      gold: i % 3 === 0,
    }));
  }, [count]);

  return (
    <div className={`ow-incense ${className ?? ''}`} aria-hidden="true">
      {plumes.map((p, i) => (
        <span
          key={i}
          className={`ow-smoke ${p.gold ? 'ow-smoke-gold' : ''}`}
          style={
            {
              left: `${p.x}%`,
              width: `${p.width}px`,
              height: `${p.width}px`,
              ['--delay' as never]: `${-p.delay}s`,
              ['--dur' as never]: `${p.dur}s`,
              ['--drift' as never]: `${p.drift}px`,
              ['--scale' as never]: `${p.scale}`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
